import React, { useState } from "react";
import { ChevronUp, ChevronDown, X, MapPin } from "lucide-react";
import { Button, inputClass } from "./kit";

// Where the candidate wants to work — ORDERED, because the order is load-bearing in two ways.
//
// RANKING: the backend gives a job in their #1 location a score bonus (+15, falling by 3 per rank),
// so their top choice floats up without burying a much better match one city over.
//
// WIDENING: the backend derives rungs from each entry — "Brampton, ON, Canada" -> "Ontario, Canada"
// -> "Canada" — and queries them as separate widening ROUNDS, entered only when the pool is still
// thin. That is why we ask for the parts separately rather than as one free-text line: the rungs are
// derived from the structure. (Measured 2026-09-25: Brampton returns 10 jobs, Ontario 1,477, Canada
// 2,705 — and the rungs CANNOT be merged into one query, because the narrow results get swamped.)
//
// Structured entry also protects against the bug this component replaced: a comma-separated text
// field turned one "Toronto, ON, Canada" into three separate locations every time it was re-saved.
export const LOC_MAX = 5;

// Country is a CLOSED list, not free text. Work authorisation only exists for these two — the apply
// profile keys it by country and onboarding collects it for US/CA only — so offering a third country
// would mean every application there hits a right-to-work question with nothing to answer from.
// (Jobs do exist elsewhere: measured 2026-10-03, Jobo has 19,734 in Germany and 21,459 in India. If
// work auth ever covers more countries, this list and REGIONS below are the only things to change.)
const COUNTRIES = ["Canada", "United States"];

// Province/state as a dependent list rather than a text box, because the abbreviation is both the
// natural thing to type and the worst thing to send: measured, "ON, Canada" returns 145 Jobo jobs
// where "Ontario, Canada" returns 1,477. A dropdown makes the bad input unreachable instead of
// correcting it afterwards.
export const REGIONS = {
  Canada: ["Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador", "Nova Scotia", "Northwest Territories", "Nunavut", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Yukon"],
  "United States": ["Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "District of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"],
};

export default function LocationPicker({ value, onChange, label = "Where do you want to work?" }) {
  const list = (value || []).filter(Boolean);
  // Default the country to whatever they have already chosen, so the common case is zero typing and
  // zero selecting — add "Toronto" to a Canadian list and it stays Canadian.
  const lastCountry = COUNTRIES.find((c) => (list[list.length - 1] || "").endsWith(c));
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState(lastCountry || COUNTRIES[0]);
  const full = list.length >= LOC_MAX;

  const add = (preset) => {
    const v = (preset || [city, region, country].map((x) => x.trim()).filter(Boolean).join(", ")).trim();
    if (!v || full) return;
    if (list.some((x) => x.toLowerCase() === v.toLowerCase())) return;
    onChange([...list, v]);
    // City and province clear for the next entry; the COUNTRY stays. Someone adding Toronto is far
    // more likely to add Ottawa next than to switch country — and "" is not a valid option now that
    // this is a select, so clearing it would leave the control showing nothing.
    setCity(""); setRegion("");
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">
        {label}{" "}
        <span className="font-normal text-muted-foreground">
          — in priority order, up to {LOC_MAX}
        </span>
      </label>

      {list.length > 0 && (
        <ul className="mb-2 rounded-xl border border-border divide-y divide-border overflow-hidden">
          {list.map((v, i) => (
            <li key={v} className="flex items-center gap-2 px-2.5 py-2 bg-white">
              <span className="w-5 h-5 shrink-0 rounded-md bg-[#0025D8]/[0.08] text-[#0025D8] text-[11px] font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <MapPin className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 min-w-0 truncate text-[13px] text-[#0F172A]">{v}</span>
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                aria-label={`Move ${v} up`}
                className="p-1 rounded-md text-muted-foreground hover:bg-secondary hover:text-[#0F172A] disabled:opacity-25 disabled:hover:bg-transparent">
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1}
                aria-label={`Move ${v} down`}
                className="p-1 rounded-md text-muted-foreground hover:bg-secondary hover:text-[#0F172A] disabled:opacity-25 disabled:hover:bg-transparent">
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => remove(i)} aria-label={`Remove ${v}`}
                className="p-1 rounded-md text-muted-foreground hover:bg-[#E11D48]/10 hover:text-[#E11D48]">
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!full && (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2">
          <input className={inputClass} value={city} placeholder="City (optional)"
            onChange={(e) => setCity(e.target.value)} />
          <select className={`${inputClass} cursor-pointer`} value={region} aria-label="Province or state"
            onChange={(e) => setRegion(e.target.value)}>
            <option value="">Province / State (optional)</option>
            {(REGIONS[country] || []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className={`${inputClass} cursor-pointer`} value={country} aria-label="Country"
            onChange={(e) => { setCountry(e.target.value); setRegion(""); }}>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button type="button" variant="secondary" size="sm" onClick={() => add()}>Add</Button>
        </div>
      )}

      {!full && !list.some((x) => x.toLowerCase() === "remote") && (
        <button type="button" onClick={() => add("Remote")}
          title="Remote roles — by far the largest single source of matches"
          className="mt-2 text-[12px] font-semibold px-2 py-1 rounded-lg border border-dashed border-[#0025D8]/40 text-[#0025D8] hover:bg-[#0025D8]/[0.04]">
          + Remote
        </button>
      )}

      <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
        Your first choice ranks highest. We widen the search if jobs are scarce.
      </p>
    </div>
  );
}
